import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Scale, Lock, ShieldCheck, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const useDownloadPDF = () => {
  const [loading, setLoading] = useState<null | "technical" | "executive">(null);
  const [slow, setSlow] = useState(false);

  const download = async (type: "technical" | "executive") => {
    setLoading(type);
    setSlow(false);
    const slowTimer = setTimeout(() => setSlow(true), 25000);
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 15000);
    const errorMessage = "PDF could not be loaded. Please try again.";
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/export-document?type=${type}`,
        { signal: controller.signal },
      );
      if (!response.ok) {
        toast.error(errorMessage);
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        type === "technical"
          ? "ClearSlot-Technical-Brief.pdf"
          : "ClearSlot-Executive-Summary.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(errorMessage);
    } finally {
      clearTimeout(slowTimer);
      clearTimeout(abortTimer);
      setLoading(null);
      setSlow(false);
    }
  };

  return { download, loading, slow };
};

const guarantees = [
  {
    icon: Scale,
    subtitle: "The CEO Guarantee",
    title: "No Competition, Ever",
    description:
      "ClearSlot does not operate a consumer booking app and never will. You own the guest. We're infrastructure underneath.",
    cta: { label: "Download Executive Summary", type: "executive" as const },
  },
  {
    icon: Lock,
    subtitle: "The CTO Guarantee",
    title: "Server-Side Hashing",
    description:
      "Phone numbers hashed server-side on receipt. Raw identifiers exist only in HTTPS transit, never persisted. Same guest, same hash across the network — no platform stores personal data.",
    cta: { label: "Download Technical Brief", type: "technical" as const },
  },
  {
    icon: ShieldCheck,
    subtitle: "The GDPR Guarantee",
    title: "EU Data Sovereignty",
    description:
      "Built in Denmark. Hosted in the EU. Data never leaves EU jurisdiction. Event data exportable on request — any format, any time. No lock-in.",
    cta: null,
  },
];

const NeutralityPromise = () => {
  const { download, loading, slow } = useDownloadPDF();

  return (
    <section id="neutrality" className="relative py-12 md:py-24 overflow-hidden bg-muted/30">
      <div className="container relative mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-sm font-medium text-amber-500">
            Neutrality
          </div>
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            We are infrastructure.
            <br />
            <span className="text-primary">We will never be your competitor.</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Three contractual commitments. Each is documented, downloadable, and signed.
          </p>
        </motion.div>

        {/* Mobile: accordion */}
        <Accordion
          type="single"
          collapsible
          defaultValue={guarantees[0].title}
          className="md:hidden space-y-3"
        >
          {guarantees.map((guarantee) => (
            <AccordionItem
              key={guarantee.title}
              value={guarantee.title}
              className="rounded-xl border border-border bg-card px-4"
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary/15 shrink-0">
                    <guarantee.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500 mb-0.5">
                      {guarantee.subtitle}
                    </p>
                    <h3 className="text-base font-bold text-foreground leading-tight">
                      {guarantee.title}
                    </h3>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-4 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {guarantee.description}
                  </p>
                  {guarantee.cta && (
                    <div className="flex flex-col items-start gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="font-mono text-xs min-h-[44px] md:min-h-0"
                        onClick={() => download(guarantee.cta!.type)}
                        disabled={loading !== null}
                      >
                        {loading === guarantee.cta.type ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            Preparing document…
                          </>
                        ) : (
                          <>
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            {guarantee.cta.label}
                          </>
                        )}
                      </Button>
                      {loading === guarantee.cta.type && slow && (
                        <span className="text-xs text-muted-foreground">
                          This is taking longer than expected. Please wait…
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Desktop: grid */}
        <div className="hidden md:grid grid-cols-1 gap-8 md:grid-cols-3">
          {guarantees.map((guarantee, index) => (
            <motion.div
              key={guarantee.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card className="h-full bg-card border-border hover:border-primary/30 transition-colors">
                <CardContent className="p-8 text-center flex flex-col items-center h-full">
                  <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
                    <guarantee.icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="mb-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-500">
                    {guarantee.subtitle}
                  </div>
                  <h3 className="mb-3 text-xl font-bold text-foreground">
                    {guarantee.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed flex-1">
                    {guarantee.description}
                  </p>
                  {guarantee.cta ? (
                    <div className="mt-5 flex flex-col items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="font-mono text-xs min-h-[44px] md:min-h-0"
                        onClick={() => download(guarantee.cta!.type)}
                        disabled={loading !== null}
                      >
                        {loading === guarantee.cta.type ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            Preparing document…
                          </>
                        ) : (
                          <>
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            {guarantee.cta.label}
                          </>
                        )}
                      </Button>
                      {loading === guarantee.cta.type && slow && (
                        <span className="text-xs text-muted-foreground">
                          This is taking longer than expected. Please wait…
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-5 h-9" />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NeutralityPromise;
